import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { apiPut } from '../../lib/api.js';
import { US_STATES } from '../../lib/usConstants.js';

const input = 'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-civix-purple/60 focus:outline-hidden';

/** Fields the form edits, in order. `kind` drives the input type. */
export const FIELDS = Object.freeze([
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'phone', label: 'Phone number', kind: 'tel' },
  { key: 'email', label: 'Email', kind: 'email' },
  { key: 'address', label: 'Street address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State', kind: 'state' },
  { key: 'zip', label: 'ZIP code' },
  { key: 'birthday', label: 'Date of birth', kind: 'date' },
  { key: 'mortgageAmount', label: 'Coverage amount ($)', kind: 'number' },
]);

/** Form values from a lead (birthday as YYYY-MM-DD). */
export function toForm(lead = {}) {
  const out = {};
  for (const f of FIELDS) out[f.key] = f.key === 'birthday' ? (lead.birthday ? new Date(lead.birthday).toISOString().slice(0, 10) : '') : String(lead[f.key] ?? '');
  return out;
}

/** Only the fields that changed, shaped for PUT /leads/:id (empty text → null, birthday → UTC midnight). */
export function diffForm(lead, form) {
  const before = toForm(lead);
  const out = {};
  for (const f of FIELDS) {
    const v = String(form[f.key] ?? '').trim();
    if (v === before[f.key].trim()) continue;
    if (f.key === 'birthday') out.birthday = v ? `${v}T00:00:00.000Z` : null;
    else out[f.key] = v === '' ? null : v;
  }
  return out;
}

/** Why the form can't be saved, or ''. */
export function formProblem(form) {
  if (!form.firstName.trim() && !form.lastName.trim()) return 'A lead needs a name';
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Enter a valid email address';
  if (form.phone.replace(/\D/g, '').length < 10) return 'Enter a valid phone number';
  return '';
}

/**
 * "Lead Details" editor: all the main fields in one form, with Save and Cancel (Close reverts unsaved edits).
 * @param {{lead: object, leadId: string, onClose: () => void, onSaved?: () => void}} props
 */
export default function EditLeadModal({ lead, leadId, onClose, onSaved }) {
  const [form, setForm] = useState(() => toForm(lead));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    const problem = formProblem(form);
    if (problem) { setError(problem); return; }
    const changes = diffForm(lead, form);
    if (!Object.keys(changes).length) { onClose(); return; }
    setSaving(true);
    setError('');
    try {
      await apiPut(`/leads/${leadId}`, changes);
      toast.success('Lead details saved');
      onSaved?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not save the lead';
      setError(msg);
      toast.error(msg);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Edit lead details" data-testid="edit-lead-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={save} className="panel max-h-[92vh] w-full max-w-md space-y-3 overflow-y-auto p-5">
        <h3 className="text-lg font-semibold">Lead Details</h3>
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="mb-1 block text-[11px] text-white/50">{f.label}</span>
            {f.kind === 'state' ? (
              <select value={form.state} onChange={set('state')} className={input} data-testid="edit-state">
                <option value="" className="text-black">—</option>
                {US_STATES.map((s) => <option key={s.code} value={s.code} className="text-black">{s.name}</option>)}
              </select>
            ) : (
              <input
                type={f.kind === 'number' ? 'text' : f.kind || 'text'}
                inputMode={f.kind === 'number' ? 'decimal' : undefined}
                value={form[f.key]}
                onChange={set(f.key)}
                className={input}
                data-testid={`edit-${f.key}`}
                autoComplete="off"
              />
            )}
          </label>
        ))}
        {error && <p className="text-xs text-rose-300" role="alert" data-testid="edit-error">{error}</p>}
        <div className="flex justify-between pt-1">
          <button type="button" onClick={onClose} className="rounded-lg border border-civix-purple/50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-civix-purpleSoft hover:bg-civix-purple/10" data-testid="edit-cancel">Close</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-civix-purple px-5 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-civix-purple/90 disabled:opacity-50" data-testid="edit-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
